import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { FALLBACK_SETTINGS } from '../config/school';

// Single place the app reads school settings and the grade list from.
//   settings  school details (database value, or the built-in fallback until it loads / if it fails)
//   grades    ordered grade list, or null while loading (gradesError is set if it could not be loaded)
//   refresh   re-read both (the admin settings screen calls it after saving)
const SchoolContext = createContext(null);

export const SchoolProvider = ({ children }) => {
  const [settings, setSettings] = useState(FALLBACK_SETTINGS);
  const [settingsLoaded, setSettingsLoaded] = useState(false); // false while showing the built-in fallback
  const [grades, setGrades] = useState(null);
  const [gradesError, setGradesError] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [settingsResult, gradesResult] = await Promise.all([
        supabase.rpc('get_school_settings'),
        supabase.from('grades')
          .select('key, label, program, level, sort_order, is_eligible')
          .order('sort_order', { ascending: true })
      ]);
      if (cancelled) return;
      if (!settingsResult.error && settingsResult.data) {
        setSettings({ ...FALLBACK_SETTINGS, ...settingsResult.data });
        setSettingsLoaded(true);
      }
      if (gradesResult.error || !Array.isArray(gradesResult.data)) {
        setGradesError(true);
      } else {
        setGrades(gradesResult.data);
        setGradesError(false);
      }
    })().catch(() => {
      if (!cancelled) setGradesError(true);
    });
    return () => {
      cancelled = true;
    };
  }, [version]);

  const value = useMemo(() => {
    const list = grades ?? [];
    const labelByKey = Object.fromEntries(list.map((g) => [g.key, g.label]));
    const secondary = list.filter((g) => g.program === 'secondary' && g.level != null);
    return {
      settings,
      settingsLoaded,
      grades,
      gradesError,
      eligibleGrades: list.filter((g) => g.is_eligible),
      gradeLabel: (key) => labelByKey[key] ?? key,
      // "Grades 1-10", derived from the grade list so every page quotes the same range.
      secondaryRange: secondary.length
        ? `Grades ${Math.min(...secondary.map((g) => g.level))}-${Math.max(...secondary.map((g) => g.level))}`
        : null,
      refresh: () => setVersion((v) => v + 1)
    };
  }, [settings, settingsLoaded, grades, gradesError]);

  return <SchoolContext.Provider value={value}>{children}</SchoolContext.Provider>;
};

export const useSchool = () => {
  const context = useContext(SchoolContext);
  if (!context) throw new Error('useSchool must be used within a SchoolProvider');
  return context;
};
